import React, { useState, useEffect } from 'react';
import {Link, useNavigate} from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { IMAGES } from '../layout/theme';
import blogService from '../services/blogService';
import api from '../services/api';

// Get API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8083';

// Recent posts will be fetched from API

// Removed hardcoded data - now fetched from API

interface BlogSidebarProps {
    onCategoryFilter?: (category: string) => void;
    onTagFilter?: (tag: string) => void;
    selectedCategory?: string | null;
    selectedTag?: string | null;
}

const BlogSidebar: React.FC<BlogSidebarProps> = ({
    onCategoryFilter,
    onTagFilter,
    selectedCategory,
    selectedTag
}) => {
    const { t } = useTranslation();
    const Nav = useNavigate();
    const [categories, setCategories] = useState<Array<{id: number, name: string, post_count?: number}>>([]);
    const [tags, setTags] = useState<Array<{id: number, name: string, post_count?: number}>>([]);
    const [recentPosts, setRecentPosts] = useState<Array<{public_id: string, title: string, slug: string, publish_date: string, featured_image?: string}>>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadCategoriesAndTags();
    }, []);

    const loadCategoriesAndTags = async () => {
        try {
            setLoading(true);
            // Load categories with post counts using the with-counts endpoint
            const categoriesData = await api.get('/api/blog/categories/with-counts');
            if (categoriesData.success && categoriesData.data) {
                setCategories(categoriesData.data);
            }

            // Load tags with post counts using the with-counts endpoint
            const tagsData = await api.get('/api/blog/tags/with-counts');
            if (tagsData.success && tagsData.data) {
                setTags(tagsData.data);
            }

            // Load recent posts from API
            const recentData = await api.get('/api/blog/posts/recent?limit=4');
            if (recentData.success && recentData.data) {
                setRecentPosts(recentData.data);
            }
        } catch (error) {
            console.error('Error loading categories and tags:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="col-lg-4">
                <div className="blog-sidebar">
                    <div className="recent-post">
                        <h3 className="sidebar-title">{t('blogSidebar.recentPost')}</h3>
                        {loading ? (
                            <div className="text-center py-3">
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : recentPosts.length > 0 ? (
                            recentPosts.map((item, ind)=>(
                                <div className="recent-post-box" key={ind}>
                                    <div
                                        className="recent-post-img back-img"
                                        style={{
                                            backgroundImage: item.featured_image
                                                ? `url(${API_BASE_URL}${item.featured_image})`
                                                : `url(${IMAGES.recent1})`
                                        }}
                                    ></div>
                                    <div className="recent-post-text">
                                        <h6><Link to={`/blog-detail?slug=${item.slug}`}>{item.title}</Link></h6>
                                        <p><i className="fa fa-calendar"/> {new Date(item.publish_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-muted small">No recent posts available</p>
                        )}
                    </div>
                    <div className="categories">
                        <h3 className="sidebar-title">{t('blogSidebar.category')}</h3>
                        {loading ? (
                            <div className="text-center py-3">
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <ul>
                                {categories.map((item, i)=>(
                                    <li key={i} className={selectedCategory === item.name ? 'active' : ''}>
                                        <Link
                                            to={"#"}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                onCategoryFilter && onCategoryFilter(item.name);
                                            }}
                                            style={{ fontWeight: selectedCategory === item.name ? 'bold' : 'normal' }}
                                        >
                                            <span className="categories-text">{item.name}</span>
                                            <span>{String(item.post_count || 0).padStart(2, '0')}</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className="advertising">
                        <div className="advertising-box back-img" style={{backgroundImage:`url(${IMAGES.advertising})`}}>
                            <div className="advertising-text">
                                <h3 className="sidebar-title">{t('blogSidebar.advertising')}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="tag">
                        <h3 className="sidebar-title">{t('blogSidebar.tags')}</h3>
                        {loading ? (
                            <div className="text-center py-3">
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <ul>
                                {tags.map((data, i)=>(
                                    <li
                                        key={i}
                                        className={selectedTag === data.name ? 'active' : ''}
                                    >
                                        <Link
                                            to={"#"}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                onTagFilter && onTagFilter(data.name);
                                            }}
                                            style={{
                                                fontWeight: selectedTag === data.name ? 'bold' : 'normal',
                                                color: selectedTag === data.name ? '#ff6b6b' : 'inherit'
                                            }}
                                        >
                                            {data.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>   
        </>
    );
};

export default BlogSidebar;